import { PageNavigation } from "../../components/layout/PageNavigation";
import { PageTitleRow } from "../../components/layout/PageTitleRow";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, RefreshCw, Search, Settings2 } from "lucide-react";
import { AppLayout, PageBackLink } from "../../components/layout";
import { ContentCard, EmptyState, LoadingState, ui } from "../../components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { cn } from "../../lib/utils";
import {
  SETTINGS_VALUE_MAX_LENGTH,
  fetchOrgSettings,
  fetchOrgSettingsByCategory,
  updateOrgSetting,
} from "../../services/orgSettingsService";

const ALL = "all";


function groupByCategory(settings) {
  const groups = [];
  settings.forEach((setting) => {
    const category = String(setting?.settingsCategory ?? "").trim();
    const current = groups[groups.length - 1];
    if (current && current.category === category) current.rows.push(setting);
    else groups.push({ category, rows: [setting] });
  });
  return groups;
}

const YES = "Y";
const NO = "N";


function isYesNoSetting(setting) {
  return /^[YN]$/i.test(String(setting?.settingsValue ?? "").trim());
}

/** Yes/no rows are rendered with the same switch the policy screens use. */
function Switch({ checked, onChange, disabled, id, label }) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative h-6 w-11 shrink-0 rounded-full transition-colors cursor-pointer",
        "focus:outline-hidden focus:ring-2 focus:ring-accent/30",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        checked ? "bg-primary" : "bg-gray-300"
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform",
          checked && "translate-x-5"
        )}
      />
    </button>
  );
}


function SettingsCard({ title, count, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = `${useId()}-panel`;

  // Follows the filters: narrowing the page opens the cards, clearing it
  // closes them again.
  useEffect(() => setOpen(defaultOpen), [defaultOpen]);

  return (
    <section className="overflow-hidden rounded-2xl border border-white/60 bg-white shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)]">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          "flex w-full cursor-pointer items-center gap-2.5 px-4 py-3.5 text-left transition-colors hover:bg-workspace/30 sm:px-5",
          open && "border-b border-(--color-text-border)/60"
        )}
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
          <Settings2 className="h-4 w-4" />
        </span>
        <h2 className="m-0 min-w-0 truncate text-[15px] font-semibold text-foreground">{title}</h2>
        <span className="shrink-0 rounded-full bg-workspace px-2 py-0.5 text-[11px] font-bold per-file-hint">
          {count}
        </span>
        <ChevronDown
          className={cn(
            "ml-auto h-4 w-4 shrink-0 per-file-hint transition-transform",
            !open && "-rotate-90"
          )}
        />
      </button>

      {open ? <div id={panelId}>{children}</div> : null}
    </section>
  );
}

/**
 * Rows whose value is never shown in the clear.
 *
 * The row is offered for editing like any other, but its box masks what it
 * holds — both the stored value and what is being typed over it — so a
 * password never sits on screen for anyone passing the desk. There is
 * deliberately no reveal control: nothing on this screen needs to read it
 * back, only to replace it.
 */
const MASKED_SETTINGS = /email.?password/i;

function isMaskedSetting(setting) {
  return MASKED_SETTINGS.test(
    `${setting?.settingsKey ?? ""} ${setting?.settingsName ?? ""}`
  );
}

/** Matched on what the row actually shows: its name and its category. */
function matchesSearch(setting, term) {
  if (!term) return true;
  return [setting?.settingsName, setting?.settingsCategory]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(term);
}

export function OrgSettingsPage() {
  const { t } = useTranslation();

  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  /**
   * Every category the tenant has, kept apart from the rows on screen.
   *
   * It can only be learnt from a full read, so it is captured then and left
   * alone while a single category is being shown — otherwise choosing one
   * would collapse the filter to the only option it could still see.
   */
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(ALL);

  /** Edited values, by key. A key present here is a key the user touched. */
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState("");

  /** Per row, because saving is per row: which are in flight, which failed. */
  const [savingKeys, setSavingKeys] = useState(() => new Set());
  const [rowErrors, setRowErrors] = useState({});
  const [savedKeys, setSavedKeys] = useState(() => new Set());

  /**
   * The read follows the category filter: "all" reads everything, a category
   * reads that category from the server rather than hiding rows the screen is
   * already holding.
   *
   * Edits are NOT cleared here. They are keyed by settings key, so an edit to
   * a row that is no longer on screen simply sits inert — it is not saved, and
   * it comes back with its row.
   */
  const load = useCallback(() => {
    setLoading(true);
    setLoadError("");
    const request =
      category === ALL ? fetchOrgSettings() : fetchOrgSettingsByCategory(category);

    request
      .then((rows) => {
        setSettings(rows);
        if (category === ALL) {
          setCategories([
            ...new Set(
              rows.map((row) => String(row?.settingsCategory ?? "").trim()).filter(Boolean)
            ),
          ]);
        }
      })
      .catch((error) => {
        console.error("Failed to load org settings:", error);
        setSettings([]);
        setLoadError(error.message || t("orgSettings.errorLoad"));
      })
      .finally(() => setLoading(false));
  }, [category, t]);

  useEffect(() => load(), [load]);

  const valueOf = useCallback(
    (setting) =>
      Object.prototype.hasOwnProperty.call(edits, setting.settingsKey)
        ? edits[setting.settingsKey]
        : setting.settingsValue ?? "",
    [edits]
  );

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    return groupByCategory(
      settings.filter((setting) => matchesSearch(setting, term))
    );
  }, [settings, search]);

  /** Typing in a row clears what the last save said about it. */
  const setValue = (settingsKey, value) => {
    setEdits((current) => ({ ...current, [settingsKey]: value }));
    setRowErrors((current) => {
      if (!current[settingsKey]) return current;
      const next = { ...current };
      delete next[settingsKey];
      return next;
    });
    setSavedKeys((current) => {
      if (!current.has(settingsKey)) return current;
      const next = new Set(current);
      next.delete(settingsKey);
      return next;
    });
  };

  const withKey = (setter, settingsKey, add) =>
    setter((current) => {
      const next = new Set(current);
      if (add) next.add(settingsKey);
      else next.delete(settingsKey);
      return next;
    });

  /** One setting, one request. */
  const saveSetting = async (setting) => {
    const settingsKey = setting.settingsKey;
    const value = valueOf(setting);
    if (value.length > SETTINGS_VALUE_MAX_LENGTH) return;

    withKey(setSavingKeys, settingsKey, true);
    setRowErrors((current) => {
      const next = { ...current };
      delete next[settingsKey];
      return next;
    });

    try {
      await updateOrgSetting(settingsKey, value);
      // Saved: this is the stored value now, and no longer an edit.
      setSettings((current) =>
        current.map((row) =>
          row.settingsKey === settingsKey ? { ...row, settingsValue: value } : row
        )
      );
      setEdits((current) => {
        const next = { ...current };
        delete next[settingsKey];
        return next;
      });
      withKey(setSavedKeys, settingsKey, true);
    } catch (error) {
      console.error(`Failed to save setting ${settingsKey}:`, error);
      setRowErrors((current) => ({
        ...current,
        [settingsKey]: error.message || t("orgSettings.errorSave"),
      }));
    } finally {
      withKey(setSavingKeys, settingsKey, false);
    }
  };

  return (
    <AppLayout formLayout="standard">

      <PageNavigation>
        <div className="page-header w-full">
          <PageTitleRow back={<PageBackLink to="/" />}>
            <h1 className={ui.pageTitle}>{t("orgSettings.title")}</h1>
            <p className="text-[12px] per-file-hint">
              {t("orgSettings.subtitle")}
            </p>
          </PageTitleRow>
        </div>
      </PageNavigation>

      {loadError ? (
        <div className="mb-4 flex flex-col gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 self-start rounded-lg border border-rose-300 bg-white px-3 text-[11px] font-bold text-rose-700 transition hover:bg-rose-100 sm:self-auto"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("orgSettings.retry")}
          </button>
        </div>
      ) : null}

      {!loading && (settings.length > 0 || category !== ALL || search) ? (
        <div className="mb-4 flex flex-wrap items-center gap-2.5">
          <div className="relative w-full sm:w-[280px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 per-file-hint" />
            <input data-page-search="true"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("orgSettings.searchPlaceholder")}
              className={cn(ui.input, "h-9 w-full pl-9")}
            />
          </div>

          {categories.length > 0 ? (
            <div className="w-full sm:w-[220px]">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-9 w-full rounded-lg border border-(--color-text-border) bg-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  <SelectItem value={ALL}>{t("orgSettings.allCategories")}</SelectItem>
                  {categories.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <ContentCard>
          <LoadingState message={t("orgSettings.loading")} />
        </ContentCard>
      ) : groups.length === 0 ? (
        <ContentCard>
          <EmptyState
            icon={Settings2}
            title={
              search || category !== ALL
                ? t("orgSettings.emptyFilteredTitle")
                : t("orgSettings.emptyTitle")
            }
            message={
              search || category !== ALL
                ? t("orgSettings.emptyFilteredMessage")
                : t("orgSettings.emptyMessage")
            }
          />
        </ContentCard>
      ) : (
        <div className="grid grid-cols-1 items-start gap-5 pb-4 lg:grid-cols-2 form-grid">
          {groups.map((group) => (
            <SettingsCard
              key={group.category || "uncategorised"}
              title={group.category || t("orgSettings.uncategorised")}
              count={group.rows.length}
              defaultOpen={Boolean(search.trim()) || category !== ALL}
            >
              <div className="divide-y divide-(--color-text-border)/40">
                {group.rows.map((setting) => {
                  const settingsKey = setting.settingsKey;
                  const value = valueOf(setting);
                  const over = value.length > SETTINGS_VALUE_MAX_LENGTH;
                  const dirty = value !== (setting.settingsValue ?? "");
                  const isSaving = savingKeys.has(settingsKey);
                  const yesNo = isYesNoSetting(setting);
                  const masked = isMaskedSetting(setting);
                  const on = value.trim().toUpperCase() === YES;
                  const isSaved = savedKeys.has(settingsKey);
                  const rowError = rowErrors[settingsKey];

                  return (
                    <div
                      key={settingsKey}
                      className="flex flex-col gap-1.5 px-4 py-3.5 sm:px-5"
                    >
                      {/* The label sits above its field at every width: the
                          cards are half the page wide, so a label column would
                          leave the value nowhere to go. */}
                      <label
                        htmlFor={`setting-${settingsKey}`}
                        className="min-w-0 text-sm font-semibold text-foreground"
                      >
                        {setting.settingsName}
                      </label>

                      <div className="min-w-0">
                        {/* Field and its Save stay on one line at every width:
                            the button belongs to the box beside it. */}
                        <div className="flex items-center gap-2">
                          {yesNo ? (
                            // The switch states the value; Save still commits
                            // it, so a mis-tap can be undone before it is sent.
                            <div className="flex h-10 min-w-0 flex-1 items-center gap-2.5">
                              <Switch
                                id={`setting-${settingsKey}`}
                                checked={on}
                                disabled={isSaving}
                                label={setting.settingsName}
                                onChange={(next) => setValue(settingsKey, next ? YES : NO)}
                              />
                              <span className="text-[13px] font-semibold per-file-hint">
                                {t(on ? "orgSettings.enabled" : "orgSettings.disabled")}
                              </span>
                            </div>
                          ) : (
                            <input
                              id={`setting-${settingsKey}`}
                              type={masked ? "password" : "text"}
                              value={value}
                              disabled={isSaving}
                              autoComplete={masked ? "new-password" : "off"}
                              onChange={(event) => setValue(settingsKey, event.target.value)}
                              className={cn(
                                ui.input,
                                "h-10 min-w-0 flex-1",
                                (over || rowError) && "border-rose-500"
                              )}
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => saveSetting(setting)}
                            disabled={!dirty || over || isSaving}
                            className={cn(
                              "h-10 shrink-0 rounded-xl px-3 text-[13px] font-semibold transition sm:px-4",
                              dirty && !over
                                ? "bg-primary text-white hover:opacity-90"
                                : "border border-(--color-text-border) per-file-hint",
                              "disabled:cursor-not-allowed disabled:opacity-60"
                            )}
                          >
                            {isSaving ? t("orgSettings.saving") : t("orgSettings.save")}
                          </button>
                        </div>

                        {over ? (
                          <p className="mt-1 text-[11px] font-semibold text-rose-500">
                            {t("orgSettings.errorTooLong", { max: SETTINGS_VALUE_MAX_LENGTH })}
                          </p>
                        ) : rowError ? (
                          <p className="mt-1 text-[11px] font-semibold text-rose-600">{rowError}</p>
                        ) : isSaved ? (
                          <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                            <Check className="h-3.5 w-3.5" />
                            {t("orgSettings.savedOne")}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </SettingsCard>
          ))}
        </div>
      )}
    </AppLayout>
  );
}

export default OrgSettingsPage;
